import React, { useState } from "react";
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Button,
  Typography,
} from "@mui/material";
import { PlayArrow } from "@mui/icons-material";
import RecipeEditor from "./RecipeEditor";
import RecipeDiff from "./RecipeDiff";
import { useNavigate } from "react-router";

interface ViewEditTabContentProps {
  selectedRecipe: string;
  useDiffEditor: boolean;
  setUseDiffEditor: (value: boolean) => void;
  editorTheme: "vs-light" | "vs-dark";
  setEditorTheme: (theme: "vs-light" | "vs-dark") => void;
  showAICopilot: boolean;
  setShowAICopilot: (show: boolean) => void;
  modifiedRecipeContent: string | null;
}

const ViewEditTabContent = ({
  selectedRecipe,
  useDiffEditor,
  setUseDiffEditor,
  editorTheme,
  setEditorTheme,
  modifiedRecipeContent,
}: ViewEditTabContentProps) => {
  const navigate = useNavigate();

  const handleTestChanges = () => {
    navigate(`/blueprint/${encodeURIComponent(selectedRecipe)}/generate`);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box>
        <Stack direction="row" spacing={3} sx={{ mb: 2 }} alignItems="center">
          {/* View Mode Toggle */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              View Mode
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={useDiffEditor ? "diff" : "editor"}
              onChange={(_, newValue) => {
                if (newValue !== null) {
                  setUseDiffEditor(newValue === "diff");
                }
              }}
              size="small"
            >
              <ToggleButton value="editor">Editor</ToggleButton>
              <ToggleButton value="diff">Diff</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {/* Theme Toggle */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
              Theme
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={editorTheme}
              onChange={(_, newValue: "vs-light" | "vs-dark" | null) => {
                if (newValue !== null) {
                  setEditorTheme(newValue);
                }
              }}
              size="small"
            >
              <ToggleButton value="vs-light">Light</ToggleButton>
              <ToggleButton value="vs-dark">Dark</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, mb: 2 }}>
        {useDiffEditor ? (
          <RecipeDiff
            recipeName={selectedRecipe}
            theme={editorTheme}
            modifiedContent={modifiedRecipeContent}
          />
        ) : (
          <RecipeEditor
            recipeName={selectedRecipe}
            theme={editorTheme}
            modifiedContent={modifiedRecipeContent}
          />
        )}
      </Box>

      {/* Test Changes button at bottom right */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "auto" }}>
        <Button
          variant="contained"
          startIcon={<PlayArrow />}
          onClick={handleTestChanges}
        >
          Test Changes
        </Button>
      </Box>
    </Box>
  );
};

export default ViewEditTabContent;
